import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';
import { compact } from 'lodash-es';
import { VariableSizeList } from 'react-window';
import { ArrowSolidDown } from '../../icons';
import { Spinner } from '../Spinner';
import { TextField } from '../TextField';
import { useAutocompleteStyles } from './Autocomplete.styles';
// popup options virtualization implemented based on https://v4.mui.com/components/autocomplete/#virtualization
const LISTBOX_PADDING = 8; // px
function renderRow(props) {
    const { data, index, style } = props;
    return /*#__PURE__*/ React.cloneElement(data[index], {
        style: _object_spread_props(_object_spread({}, style), {
            top: style.top + LISTBOX_PADDING
        })
    });
}
const OuterElementContext = /*#__PURE__*/ React.createContext({});
const OuterElementType = /*#__PURE__*/ React.forwardRef((props, ref)=>{
    const outerProps = React.useContext(OuterElementContext);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        ref: ref
    }, props, outerProps));
});
function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(()=>{
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [
        data
    ]);
    return ref;
}
// Adapter for react-window
const ListboxComponent = /*#__PURE__*/ React.forwardRef(function ListboxComponent(props, ref) {
    const { children } = props, other = _object_without_properties(props, [
        "children"
    ]);
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 48;
    const getChildSize = (child)=>{
        var _child_props;
        const isTwoLiner = compact(child === null || child === void 0 ? void 0 : (_child_props = child.props) === null || _child_props === void 0 ? void 0 : _child_props.children.props.children).length === 2;
        return isTwoLiner ? 48 : 36;
    };
    const getHeight = ()=>{
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b)=>a + b, 0);
    };
    const gridRef = useResetCache(itemCount);
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        children: /*#__PURE__*/ _jsx(OuterElementContext.Provider, {
            value: other,
            children: /*#__PURE__*/ _jsx(VariableSizeList, {
                itemData: itemData,
                height: getHeight() + 2 * LISTBOX_PADDING,
                width: "100%",
                ref: gridRef,
                outerElementType: OuterElementType,
                innerElementType: "ul",
                itemSize: (index)=>getChildSize(itemData[index]),
                overscanCount: 5,
                itemCount: itemCount,
                children: renderRow
            })
        })
    });
});
export function Autocomplete(_0) {
    let { className, textFieldProps, loading, disableEndAdornment, virtualizeOptions, lazyOptions, disableSelectedHighlight, noOptionsText, disableTyping, menuWidth, error, helperText, collapseHelperText = true, popoverFooter } = _0, props = _object_without_properties(_0, [
        "className",
        "textFieldProps",
        "loading",
        "disableEndAdornment",
        "virtualizeOptions",
        "lazyOptions",
        "disableSelectedHighlight",
        "noOptionsText",
        "disableTyping",
        "menuWidth",
        "error",
        "helperText",
        "collapseHelperText",
        "popoverFooter"
    ]);
    const hasMultipleChips = !!props.multiple && Array.isArray(props.value) && props.value.length > 0;
    const classes = useAutocompleteStyles({
        hasMultipleChips,
        disableSelectedHighlight
    });
    const [isFocused, setIsFocused] = useState(false);
    if (virtualizeOptions) {
        props.ListboxComponent = ListboxComponent;
        props.options = isFocused && lazyOptions != null ? lazyOptions() : props.options;
    }
    return /*#__PURE__*/ _jsx(MuiAutocomplete, _object_spread_props(_object_spread({
        className: className,
        "data-testid": props.testId,
        classes: {
            option: classes.option,
            endAdornment: classes.endAdornment,
            popper: classes.popper,
            paper: classes.paper
        },
        renderInput: (params)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                disableMobile: true,
                collapseHelperText: collapseHelperText,
                inputRootClass: classes.inputRoot,
                error: !!error,
                helperText: helperText
            }, textFieldProps, params), {
                inputProps: _object_spread_props(_object_spread({}, params.inputProps), {
                    readOnly: disableTyping || false
                }),
                testId: props.testId && "".concat(props.testId, "-textfield"),
                suffix: !disableEndAdornment && /*#__PURE__*/ _jsxs("span", {
                    style: {
                        marginBlockStart: 2,
                        marginInlineEnd: 24
                    },
                    children: [
                        loading ? /*#__PURE__*/ _jsx(Spinner, {
                            testId: "".concat(props.testId, "-spinner"),
                            size: 32
                        }) : null,
                        params.InputProps.endAdornment
                    ]
                })
            })),
        popupIcon: /*#__PURE__*/ _jsx("div", {
            "data-testid": "".concat(props.testId, "-popup-icon"),
            children: /*#__PURE__*/ _jsx(ArrowSolidDown, {})
        }),
        ListboxProps: {
            'data-testid': "".concat(props.testId, "-listbox")
        },
        loading: loading,
        noOptionsText: /*#__PURE__*/ _jsx("div", {
            "data-testid": "".concat(props.testId, "-no-options-text"),
            children: noOptionsText
        })
    }, props), {
        PaperComponent: (paperProps)=>/*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({}, paperProps), {
                style: {
                    width: menuWidth
                },
                children: [
                    paperProps.children,
                    popoverFooter && popoverFooter
                ]
            })),
        onFocus: (ev)=>{
            var _props_onFocus;
            setIsFocused(true);
            (_props_onFocus = props.onFocus) === null || _props_onFocus === void 0 ? void 0 : _props_onFocus.call(props, ev);
        },
        onBlur: (ev)=>{
            var _props_onBlur;
            setIsFocused(false);
            (_props_onBlur = props.onBlur) === null || _props_onBlur === void 0 ? void 0 : _props_onBlur.call(props, ev);
        }
    }));
}
